<template>
  <div class="action-list">
    <div class="header">
      <h3>Activities</h3>
      <span v-if="actions.length" class="count-badge">{{ actions.length }}</span>
    </div>

    <div class="list-body">
      <!-- Display Message if No Actions are Available -->
      <div v-if="!actions.length" class="no-actions">
        No activities available.
      </div>

      <!-- Actions List with Drag-and-Drop -->
      <div v-else class="actions-container">
        <div
          v-for="(action, index) in sortedActions"
          :key="action.id"
          class="action-item"
          :class="{ 'is-dragging': draggedActionId === action.id }"
          draggable="true"
          @dragstart="handleDragStart($event, action, index)"
          @dragover="handleDragOver($event, index)"
          @drop="handleDrop($event, action, index)"
          @dragend="handleDragEnd"
        >
          <div class="drag-handle" title="Drag to reorder">
            <i class="bi bi-grip-vertical"></i>
          </div>

          <div class="order-badge">{{ action.order }}</div>

          <div class="action-content">
            <h4 class="action-name">
              <a @click.prevent="redirectToAction(action.id)" class="action-link">
                {{ action.name }}
              </a>
            </h4>
            <p class="action-description">{{ action.description }}</p>
            <div class="action-meta">
              <span class="status-badge" :class="'status-' + (action.status || 'unknown').toLowerCase().replace(/\s+/g, '-')">
                {{ action.status }}
              </span>
              <span class="location-name" v-if="getLocationName(action.location_id)">
                <i class="bi bi-geo-alt"></i> {{ getLocationName(action.location_id) }}
              </span>
            </div>
          </div>

          <div v-if="reorderingActionId === action.id" class="loading-spinner">
            <i class="bi bi-hourglass-split"></i>
          </div>
        </div>

        <div v-if="dragOverIndex !== null" class="drop-zone-indicator" :style="{ top: (dragOverIndex * 100) + 'px' }">
          Drop here
        </div>
      </div>

      <!-- Form to Add New Action -->
      <div class="add-action-form">
        <div class="form-row">
          <div class="form-group">
            <input
              type="text"
              v-model="newAction.name"
              placeholder="Activity Name"
              :class="{ 'input-error': showError && !newAction.name, 'input-field': true }"
            />
          </div>
          <div class="form-group">
            <input
              type="text"
              v-model="newAction.description"
              placeholder="Description"
              :class="{ 'input-error': showError && !newAction.description, 'input-field': true }"
            />
          </div>
          <div class="form-group">
            <select id="status" v-model="newAction.status" class="input-field">
              <option disabled value="">Status</option>
              <option value="Contract">Contract</option>
              <option value="To be started">To be started</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Complete">Complete</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          <div class="form-group">
            <select v-model="newAction.location_id" class="input-field">
              <option disabled value="">Select a Location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">
                {{ location.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="button-row">
          <button class="add-btn" @click="addAction">Add Activity</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config";
import apiService from "@/services/apiService";
import { useToast } from "vue-toastification";
import { mapState } from "vuex";

export default {
  props: {
    actions: {
      type: Array,
      required: true
    },
    workflowId: {
      type: Number,
      required: true
    },
    workflowName: {
      type: String,
      default: '',
    },
    projectID: {
      type: Number,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      newAction: {
        name: '',
        description: '',
        status: '',
        location_id: ''
      },
      locations: [],
      showError: false,
      draggedActionId: null,
      draggedIndex: null,
      dragOverIndex: null,
      reorderingActionId: null,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    sortedActions() {
      return [...this.actions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.fetchLocations();
  },
  methods: {
    async fetchLocations() {
      try {
        this.locations = await apiService.fetchResources(API_ENDPOINTS.locations);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    },

    // Drag-and-drop handlers
    handleDragStart(event, action, index) {
      this.draggedActionId = action.id;
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.currentTarget);
    },

    handleDragOver(event, index) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        this.dragOverIndex = index;
      }
    },

    async handleDrop(event, targetAction, targetIndex) {
      event.preventDefault();
      this.dragOverIndex = null;

      if (this.draggedActionId === null || this.draggedActionId === targetAction.id) {
        return;
      }

      const draggedAction = this.sortedActions[this.draggedIndex];

      // Optimistic UI: reorder immediately
      const draggedActions = [...this.sortedActions];
      draggedActions.splice(this.draggedIndex, 1);
      draggedActions.splice(targetIndex, 0, draggedAction);

      // Emit optimistic reorder
      this.$emit('updateActionsOrder', draggedActions.map(a => a.id));

      this.reorderingActionId = draggedAction.id;

      try {
        // Call the move API endpoint
        await axios.post(
          API_ENDPOINTS.workflowActionMove(this.workflowId, draggedAction.id),
          {
            entra_id: this.loggedInUser.entra_id,
            order: targetIndex + 1  // 1-indexed
          },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        );

        this.toast.success(`Activity "${draggedAction.name}" moved successfully`);
        // Refresh actions from server to ensure UI shows correct state
        this.$emit('refreshActions');
      } catch (error) {
        console.error("Failed to reorder action:", error);
        this.toast.error("Failed to reorder activity");
        // Emit refresh to reset UI from server state
        this.$emit('refreshActions');
      } finally {
        this.reorderingActionId = null;
      }
    },

    handleDragEnd() {
      this.draggedActionId = null;
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },

    // Action management
    addAction() {
      if (this.newAction.name && this.newAction.description && this.newAction.status) {
        this.$emit('addAction', { 
          action: this.newAction, 
          workflowId: this.workflowId 
        });
        this.newAction = { name: '', description: '', status: '', location_id: '' };
        this.showError = false;
      } else {
        this.showError = true;
      }
    },

    redirectToAction(actionId) {
      this.$router.push({
        name: 'ActionDetailPage',
        params: { id: actionId },
        query: {
          workflowName: this.workflowName,
          projectID: this.projectID,
          projectName: this.projectName
        }
      });
    },

    getLocationName(locationId) {
      const location = this.locations.find(loc => loc.id === locationId);
      return location ? location.name : '';
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.action-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .count-badge {
    background: $primary-color;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    position: relative;
    top: 2px;
  }
}

.list-body {
  margin-top: 34px;
}

.actions-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  position: relative;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 2px solid transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: move;

  &:hover {
    border-color: $primary-color;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &.is-dragging {
    opacity: 0.6;
    background: #f5f5f5;
  }
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #999;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  i {
    font-size: 18px;
  }
}

.order-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: $primary-color;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

.action-content {
  flex: 1;
  min-width: 0;

  .action-name {
    margin: 0 0 4px 0;
    font-size: 15px;
    font-weight: 600;
  }

  .action-link {
    color: $primary-color;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .action-description {
    margin: 0 0 8px 0;
    font-size: 13px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .action-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
  }

  .status-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 500;
    white-space: nowrap;

    &.status-contract {
      background: #fff3cd;
      color: #856404;
    }

    &.status-to-be-started {
      background: #d1ecf1;
      color: #0c5460;
    }

    &.status-ongoing {
      background: #d4edda;
      color: #155724;
    }

    &.status-complete {
      background: #cfe2ff;
      color: #084298;
    }

    &.status-pending {
      background: #f8d7da;
      color: #721c24;
    }
  }

  .location-name {
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: $primary-color;
  animation: spin 1s linear infinite;
  flex-shrink: 0;

  i {
    font-size: 14px;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.drop-zone-indicator {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: $primary-color;
  pointer-events: none;
  transition: top 0.2s ease;
}

.add-action-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  padding: 16px 0 0 0;
  border-top: 2px solid #e8e8e8;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.form-group {
  flex: 1;
  min-width: 180px;
  display: flex;
  flex-direction: column;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
    font-size: 13px;
  }
}

.input-field {
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  height: 36px;
  font-size: 14px;
  background-color: white;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover:not(:disabled) {
    border-color: $primary-color;
    box-shadow: 0 2px 4px rgba($primary-color, 0.08);
  }

  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
}

.input-error {
  border-color: #dc3545 !important;
  background-color: #fff5f5;

  &:focus {
    box-shadow: 0 0 0 3px rgba(#dc3545, 0.1) !important;
  }
}

.button-row {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.add-btn {
  padding: 10px 24px;
  background: $primary-color;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba($primary-color, 0.15);

  &:hover {
    background: darken($primary-color, 8%);
    box-shadow: 0 4px 8px rgba($primary-color, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba($primary-color, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.no-actions {
  padding: 20px;
  text-align: center;
  font-style: italic;
  color: #999;
}
</style>
